// Guard DURABLE once-ever del disparador PASIVO de las encuestas de rollout (permanencia y
// transición/sunset), keyed por (comercio, feature). Llave dedicada en localStorage que
// resetStore(login)/logout/cambio de sucursal NO borran — a diferencia del Set en-memoria
// `passiveShownFor` de permanenceSurvey/transitionSurvey, que SÍ se vacía en esos resets
// (AuthContext.tsx:375,420) y por eso, en multisucursal, re-disparaba la encuesta "a cada rato".
//
// Mismo patrón que analytics/surveyShownGuard.claimSurveyShown y GaAnnouncementBanner.dismissKey:
// llaves con prefijo dedicado que sobreviven el resetStore (PRJ-001). El Set en-memoria queda como
// dedup barato dentro de la sesión; el gate AUTORITATIVO (once-ever de por vida por comercio+feature)
// es ESTE. El signal `requested` de cada encuesta sigue efímero (un prompt pendiente NO debe
// sobrevivir logout): esto solo persiste el hecho de que la encuesta YA se mostró.

export type PassiveSurveyKind = 'permanence' | 'transition';

// Namespaces SEPARADOS por tipo de encuesta: un comercio en control recibe transición (sunset) y un
// comercio adoptado recibe permanencia — casos disjuntos, pero se namespacean para que marcar una
// NUNCA bloquee la otra si el backend cambiara de cohorte entre resoluciones.
const PREFIX: Record<PassiveSurveyKind, string> = {
  permanence: 'plick:permanence-survey-shown:v1:',
  transition: 'plick:transition-survey-shown:v1:',
};

function shownKey(kind: PassiveSurveyKind, commerceId: string, featureKey: string): string {
  return `${PREFIX[kind]}${commerceId}:${featureKey}`;
}

// True si la encuesta pasiva ya se mostró de por vida para este (comercio, feature).
export function wasPassiveSurveyShown(kind: PassiveSurveyKind, commerceId: string, featureKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(shownKey(kind, commerceId, featureKey)) === '1';
  } catch {
    return false;
  }
}

// Claim ATÓMICO: marca el guard durable y devuelve true SOLO si esta llamada fue la que lo marcó (no
// estaba antes). El caller dispara la encuesta únicamente cuando recibe true → once-ever por
// (comercio, feature), sobreviviendo reload/logout/cambio de sucursal.
export function claimPassiveSurveyShown(kind: PassiveSurveyKind, commerceId: string, featureKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = shownKey(kind, commerceId, featureKey);
    if (window.localStorage.getItem(key) === '1') return false;
    window.localStorage.setItem(key, '1');
    return true;
  } catch {
    // Sin localStorage persistente (modo privado / quota / storage bloqueado): NO podemos garantizar
    // el once-ever, así que degradamos a NO mostrar (return false). Diferencia deliberada con
    // analytics/surveyShownGuard.claimSurveyShown, que degrada a "emitir" (return true): allá el claim
    // gatea una IMPRESIÓN de telemetría (mejor sobre-contar que perder la señal), aquí gatea MOSTRAR
    // una encuesta de UI. Devolver true re-mostraría en CADA recarga —el Set en-memoria `passiveShownFor`
    // se vacía al recargar/cambiar de sucursal—, reintroduciendo el "reaparece a cada rato" que este
    // guard existe para eliminar. La encuesta es OMITIBLE y NUNCA alimenta el KPI, así que perderla en
    // almacenamiento no persistente es preferible a badgear al operador en cada carga.
    return false;
  }
}

// Solo para tests: limpia las llaves durables de AMBOS tipos (aislamiento entre casos).
export function __resetPassiveSurveyGuardForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && (k.startsWith(PREFIX.permanence) || k.startsWith(PREFIX.transition))) toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
